import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { hubKeys } from "../../api/hub/client";
import { workspaceRecords, workspaceAction, actionKey, type OperationReceipt, type WorkspaceQuery } from '../../api/hub/workspace';
import { dateOnly } from "../tasks/useTasks";
import type { CalendarItem } from "../../api/hub/types";
export const calendarKinds = {
  task: "事项截止",
  project: "项目目标",
  milestone: "项目节点",
  delivery: "客户交付",
  approval: "审批截止",
  okr: "OKR 检查",
};
export const calendarIcons = {
  task: "□",
  project: "◇",
  milestone: "◇",
  delivery: "↗",
  approval: "✓",
  okr: "◎",
};
export function calendarDays(date: Date, mode = "month", weekStartsOn = 1) {
  const start = new Date(
    date.getFullYear(),
    date.getMonth(),
    mode === "month" ? 1 : date.getDate(),
  );
  start.setDate(start.getDate() - ((start.getDay() - weekStartsOn + 7) % 7));
  return Array.from({ length: mode === "month" ? 42 : 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return dateOnly(d);
  });
}
export const calendarSort = (a: CalendarItem, b: CalendarItem) =>
  (a.priority || 5) - (b.priority || 5) || a.title.localeCompare(b.title);
export function useCalendar(from: string, to: string, personId: string, view: WorkspaceQuery = {}) {
  const qc = useQueryClient();
  const [receipt, setReceipt] = useState<OperationReceipt | null>(null);
  const next = new Date(`${to}T12:00:00`); next.setDate(next.getDate() + 1);
  const requestedTo = dateOnly(next);
  const savedFrom = typeof view.filters?.from === 'string' ? view.filters.from : from;
  const savedTo = typeof view.filters?.to === 'string' ? view.filters.to : requestedTo;
  const range = { from: savedFrom > from ? savedFrom : from, to: savedTo < requestedTo ? savedTo : requestedTo };
  const input: WorkspaceQuery = { ...view, source: 'calendar', filters: { ...view.filters, ...range, ...(personId ? { assigneeId: personId } : {}) } };
  return {
    receipt, range,
    items: useQuery({ queryKey: ['hub', 'workspace', 'query', input], queryFn: async ({ signal }) => (await workspaceRecords<CalendarItem>(input, signal)).items }),
    move: useMutation({
      mutationFn: async ({ id, date, expectedVersion, timeZone }: { id: string; date: string; expectedVersion: number | string; timeZone: string }) => {
        const result = await workspaceAction({ action: 'calendar.reschedule', input: { id, date, expectedVersion, timeZone }, idempotencyKey: actionKey() });
        setReceipt(result.receipt); return result.data;
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: hubKeys.all }),
    }),
  };
}

export function useMobileCalendar() {
  const query = "(max-width: 600px)";
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const media = window.matchMedia(query);
    const change = () => setMobile(media.matches);
    change();
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);
  return mobile;
}
export function mobileCalendarDays(
  days: string[],
  items: CalendarItem[],
  month: number,
  mode: string,
  showEmpty: boolean,
) {
  return days.filter(
    (day) =>
      (mode === "week" || Number(day.slice(5, 7)) === month + 1) &&
      (showEmpty || items.some((i) => i.date.slice(0, 10) === day)),
  );
}
export const calendarDayLabel = (day: string) =>
  new Intl.DateTimeFormat("zh-CN", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(`${day}T12:00:00`));
